import type { AvailableUnit } from '@/core/api/models/properties';
import { useAppStore } from '@/core/stores/appStore';
import { useFocusEffect } from '@react-navigation/native';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { unitsService } from '../services/unitsService';

/** Stable reference so a failed fetch doesn't produce a new array every render. */
const EMPTY_UNITS: AvailableUnit[] = [];

/** Native scroll pages. Web uses {@link UNITS_PAGE_SIZE_WEB}. */
export const UNITS_PAGE_SIZE = 20;

/**
 * Web catalogue page. The first response is shown immediately; later pages
 * append underneath until the API reports `hasNext: false`.
 */
export const UNITS_PAGE_SIZE_WEB = 350;

/** API "no practical limit" for `max_price` (slider at the 3M+ end). */
export const UNITS_NO_PRICE_LIMIT_AED = 999_999_999;

function resolveUnitsPageSize(loadUntilEnd: boolean): number {
  if (Platform.OS === 'web' || loadUntilEnd) {
    return UNITS_PAGE_SIZE_WEB;
  }
  return UNITS_PAGE_SIZE;
}

/** Skip ids already rendered so a repeated page can't duplicate React keys. */
function flattenPages(
  pages: { units: AvailableUnit[] }[] | undefined,
): AvailableUnit[] {
  if (!pages || pages.length === 0) {
    return EMPTY_UNITS;
  }
  const seen = new Set<string>();
  const flat: AvailableUnit[] = [];
  for (const page of pages) {
    for (const unit of page.units) {
      if (unit?.unit_id && !seen.has(unit.unit_id)) {
        seen.add(unit.unit_id);
        flat.push(unit);
      }
    }
  }
  return flat.length > 0 ? flat : EMPTY_UNITS;
}

/** Card fields only, so an identical catalogue response does not rebuild the list. */
function sameListingUnits(
  left: readonly AvailableUnit[],
  right: readonly AvailableUnit[],
): boolean {
  if (left === right) {
    return true;
  }
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (
      !a ||
      !b ||
      a.unit_id !== b.unit_id ||
      a.unit_number !== b.unit_number ||
      a.unit_type !== b.unit_type ||
      a.unit_model !== b.unit_model ||
      a.number_of_bedrooms !== b.number_of_bedrooms ||
      a.number_of_bathrooms !== b.number_of_bathrooms ||
      a.total_price !== b.total_price ||
      a.total_area !== b.total_area ||
      a.handover_date !== b.handover_date ||
      a.project_name !== b.project_name ||
      a.reservation_status !== b.reservation_status ||
      a.latitude !== b.latitude ||
      a.longitude !== b.longitude
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Runs after the current frame so a catalogue refresh does not compete with
 * navigation. Cancellable, same as the old interaction-queue handle.
 */
function scheduleAfterInteractions(task: () => void): { cancel: () => void } {
  let cancelled = false;
  let frameId = 0;
  const timeoutId = setTimeout(() => {
    frameId = requestAnimationFrame(() => {
      if (!cancelled) {
        task();
      }
    });
  }, 0);
  return {
    cancel: () => {
      cancelled = true;
      clearTimeout(timeoutId);
      cancelAnimationFrame(frameId);
    },
  };
}

/**
 * Loads available units via shared `unitsService` (Bearer JWT from login), one
 * page at a time. Prefer an explicit `projectId`; otherwise uses `project_id`
 * from ADHA login details in the store. Until the first filtered fetch
 * completes, falls back to units stored at login.
 *
 * Pagination: the API reports `hasNext`.
 * Web loads the first page, paints it, then keeps requesting the next page
 * until `hasNext` is false. Native does the same only when `loadUntilEnd` is
 * set (catalogue). Other native callers still append on scroll via `loadMore()`.
 */
export const useAvailableUnits = (
  projectId?: string,
  maxPrice?: number,
  loadUntilEnd = false,
) => {
  const storedProjectId = useAppStore((s) => s.adhaLoanDetails?.projectId);
  const resolvedProjectId = projectId || storedProjectId || '';
  const setAvailableUnits = useAppStore((s) => s.setAvailableUnits);

  const pageSize = resolveUnitsPageSize(loadUntilEnd);
  const pageUntilEnd = Platform.OS === 'web' || loadUntilEnd;

  const query = useInfiniteQuery({
    queryKey:
      Platform.OS === 'web'
        ? ['availableUnits', resolvedProjectId, maxPrice ?? null]
        : ['availableUnits', resolvedProjectId, maxPrice ?? null, pageSize],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const request: {
        projectId: string;
        page: number;
        limit: number;
        maxPrice?: number;
      } = {
        projectId: resolvedProjectId,
        page: pageParam,
        limit: pageSize,
      };
      if (maxPrice != null) {
        request.maxPrice = maxPrice;
      }
      const response = await unitsService.getAvailableUnits(request);
      const units = Array.isArray(response.units) ? response.units : [];
      return { ...response, page: response.page ?? pageParam, units };
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      // If the backend explicitly says there is no next page, stop paginating
      if (lastPage.hasNext === false || String(lastPage.hasNext) === 'false') {
        return undefined;
      }

      if (!lastPage.units || lastPage.units.length === 0) {
        return undefined;
      }

      const previousPages = allPages.slice(0, -1);
      if (previousPages.length > 0) {
        const seenIds = new Set(
          previousPages.flatMap(p => p.units.map(u => u.unit_id))
        );
        const hasNewUnits = lastPage.units.some(u => u?.unit_id && !seenIds.has(u.unit_id));
        if (!hasNewUnits) {
          return undefined; // Reached end or backend is stuck returning duplicates
        }
      }

      // If the backend returns fewer items than a full page, it might be the end.
      return lastPageParam + 1;
    },
    enabled: !!resolvedProjectId,
    // A failed page shouldn't be retried on every scroll — surface it instead.
    retry: 1,
    // Price filter changes must always hit the network (incl. returning to
    // max_price=999999999 after a narrower range). Window focus still quiet.
    staleTime: 0,
    refetchOnWindowFocus: false,
    // Web: leave list → details/other → back to list remounts the screen; always
    // refetch so reserved/available units aren't stuck behind the 5 min staleTime.
    // Native keeps default (refetch only when stale).
    ...(Platform.OS === 'web' ? { refetchOnMount: 'always' as const } : {}),
  });

  // Login-time units are only shown until the first fetch lands. After that the
  // selector is constant, so copying each new page into the store does not
  // re-render the list screen a second time.
  const isQueryFetched = query.isFetched;
  const storedUnits = useAppStore((s) =>
    isQueryFetched ? EMPTY_UNITS : s.availableUnits,
  );

  const fetchedUnits = useMemo(
    () => flattenPages(query.data?.pages),
    [query.data?.pages],
  );

  const shouldHoldCatalogue = Platform.OS !== 'web' && loadUntilEnd;
  const stableUnitsRef = useRef<AvailableUnit[]>(EMPTY_UNITS);
  const pendingUnitsRef = useRef<AvailableUnit[] | null>(null);
  const [catalogueHoldVersion, setCatalogueHoldVersion] = useState(0);
  const isCatalogueRefreshRef = useRef(false);
  const isFetchingRef = useRef(false);
  isFetchingRef.current = query.isFetching;
  const didFocusCatalogueRef = useRef(false);
  const mountedRef = useRef(true);
  const [catalogueRefreshTick, setCatalogueRefreshTick] = useState(0);

  let catalogueUnits = fetchedUnits;
  if (shouldHoldCatalogue) {
    if (sameListingUnits(stableUnitsRef.current, fetchedUnits)) {
      catalogueUnits = stableUnitsRef.current;
    } else if (stableUnitsRef.current.length === 0) {
      stableUnitsRef.current = fetchedUnits;
      catalogueUnits = fetchedUnits;
    } else {
      // Keep the cards that are already on screen. The swap waits until
      // navigation and scrolling have finished.
      pendingUnitsRef.current = fetchedUnits;
      catalogueUnits = stableUnitsRef.current;
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!shouldHoldCatalogue) {
      return;
    }
    const pending = pendingUnitsRef.current;
    if (!pending || sameListingUnits(stableUnitsRef.current, pending)) {
      return;
    }
    const task = scheduleAfterInteractions(() => {
      const next = pendingUnitsRef.current;
      pendingUnitsRef.current = null;
      if (!next || sameListingUnits(stableUnitsRef.current, next)) {
        return;
      }
      stableUnitsRef.current = next;
      setCatalogueHoldVersion((version) => version + 1);
    });
    return () => task.cancel();
  }, [shouldHoldCatalogue, fetchedUnits, catalogueHoldVersion]);

  // After fetch: trust API (including empty). Before fetch: show login store units.
  // Mobile catalogue keeps the previous list until a different payload is applied.
  const units = query.isFetched ? catalogueUnits : storedUnits;

  // Keep the store aligned with the list that is actually rendered. Writing this
  // from queryFn would skip cache hits (e.g. returning to a price filter already
  // fetched), leaving unit details unable to resolve ids the grid still shows.
  useEffect(() => {
    if (!query.isFetched) {
      return;
    }
    if (useAppStore.getState().availableUnits === units) {
      return;
    }
    setAvailableUnits(units);
  }, [query.isFetched, setAvailableUnits, units]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    isSuccess,
    isFetchNextPageError,
  } = query;

  /**
   * Safe to call on every scroll event: a ref (not React state, which lags a
   * frame behind a scroll burst) keeps one page in flight, and `cancelRefetch`
   * stays false so repeat calls can't abort and restart the pending request.
   */
  const isLoadingPageRef = useRef(false);
  /**
   * Web only: bumped when a page request settles. The ref above clears in a
   * promise callback after React may already have re-rendered with the new
   * page, so without this the "pull next page" effect can miss its turn.
   */
  const [pageSettledTick, setPageSettledTick] = useState(0);
  const loadMore = useCallback(() => {
    if (
      isCatalogueRefreshRef.current ||
      isLoadingPageRef.current ||
      !hasNextPage ||
      isFetchingNextPage
    ) {
      return;
    }
    isLoadingPageRef.current = true;
    void fetchNextPage({ cancelRefetch: false }).finally(() => {
      isLoadingPageRef.current = false;
      if (Platform.OS === 'web' && mountedRef.current) {
        setPageSettledTick((tick) => tick + 1);
      }
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const refetchQuery = query.refetch;
  const refetch = useCallback(async () => {
    if (shouldHoldCatalogue) {
      isCatalogueRefreshRef.current = true;
    }
    try {
      return await refetchQuery();
    } finally {
      if (shouldHoldCatalogue) {
        isCatalogueRefreshRef.current = false;
        if (mountedRef.current) {
          setCatalogueRefreshTick((tick) => tick + 1);
        }
      }
    }
  }, [refetchQuery, shouldHoldCatalogue]);

  // Web: after the first page paints, pull the rest until `hasNext` is false.
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    if (!isSuccess || isError || isFetchNextPageError) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage || isLoadingPageRef.current) {
      return;
    }
    loadMore();
  }, [
    isSuccess,
    isError,
    isFetchNextPageError,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    query.data?.pages.length,
    pageSettledTick,
  ]);

  // Mobile catalogue only: same "until hasNext is false" paging as web, but
  // the next request waits until interactions finish so the list can paint.
  useEffect(() => {
    if (Platform.OS === 'web' || !loadUntilEnd) {
      return;
    }
    if (!isSuccess || isError || isFetchNextPageError) {
      return;
    }
    if (
      !hasNextPage ||
      isFetchingNextPage ||
      isLoadingPageRef.current ||
      isCatalogueRefreshRef.current
    ) {
      return;
    }
    const task = scheduleAfterInteractions(() => {
      loadMore();
    });
    return () => task.cancel();
  }, [
    loadUntilEnd,
    isSuccess,
    isError,
    isFetchNextPageError,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    query.data?.pages.length,
    catalogueRefreshTick,
  ]);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Mobile catalogue only: opening this screen from another page refetches
  // after the transition, without touching the web list.
  useFocusEffect(
    useCallback(() => {
      if (!shouldHoldCatalogue || !resolvedProjectId) {
        return undefined;
      }
      if (!didFocusCatalogueRef.current) {
        didFocusCatalogueRef.current = true;
        return undefined;
      }
      let cancelled = false;
      const task = scheduleAfterInteractions(() => {
        if (cancelled || isCatalogueRefreshRef.current) {
          return;
        }
        void refetchRef.current();
      });
      return () => {
        cancelled = true;
        task.cancel();
      };
    }, [resolvedProjectId, shouldHoldCatalogue]),
  );

  const lastPage = query.data?.pages?.[query.data.pages.length - 1];
  const hasMoreUnits =
    Boolean(hasNextPage) && !isError && !isFetchNextPageError;

  return {
    ...query,
    refetch,
    loadMore,
    /**
     * Scroll trigger. Web pages itself in the background, so this stays false
     * there. Native stays true only when another page can be requested.
     */
    canLoadMore: pageUntilEnd
      ? false
      : !!hasNextPage && !isFetchingNextPage && !isError,
    /**
     * True until the API reports `hasNext: false`. The Available Units count
     * stays a shimmer for this whole window, including while a later page is
     * in flight.
     */
    hasMoreUnits,
    /**
     * True only before the first page has arrived. A later page must not
     * replace the grid with the full-screen skeleton.
     */
    isFetchingFirstPage: query.isFetching && !isFetchingNextPage && !query.isFetched,
    data: {
      status: lastPage?.status ?? 'success',
      message: lastPage?.message ?? '',
      page: lastPage?.page,
      limit: lastPage?.limit,
      hasNext: lastPage?.hasNext,
      units,
    },
  };
};
